import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useState } from 'react';
import { api, apiUrl, ApiClientError } from '../lib/api';
import { ErrorNotice } from './Layout';
import { PlaudImportPanel } from './PlaudImportPanel';

export type MeetingFileCategory =
  | 'meeting_report'
  | 'audio_recording'
  | 'transcript'
  | 'summary'
  | 'attachment'
  | 'preparation'
  | 'completed_work'
  | 'other';

interface StoredMeetingFile {
  id: string;
  category: MeetingFileCategory;
  provider: 'r2' | 'nas';
  fileName: string;
  contentType: string;
  sizeBytes: number;
  status: 'uploading' | 'available' | 'failed' | 'deleted';
  calendarSyncStatus?: 'not_requested' | 'syncing' | 'synced' | 'failed';
  createdAt: string;
}

interface ExternalDocumentLink {
  id: string;
  title: string;
  url: string;
  category: MeetingFileCategory;
  createdAt: string;
  calendarSyncStatus: 'not_requested' | 'syncing' | 'synced' | 'failed';
  calendarSyncError: string | null;
}

interface UploadCandidate {
  id: string;
  file: File;
  category: MeetingFileCategory;
  status: 'ready' | 'hashing' | 'uploading' | 'linking' | 'done' | 'failed';
  progress: number;
  error: string | null;
  message?: string;
}

interface UploadInit {
  id: string;
  provider: 'r2' | 'nas';
  uploadUrl?: string;
  partBytes?: number;
  totalParts?: number;
  authorizePartUrl?: string;
  completeUrl?: string;
  resumed?: boolean;
}

const categories: Array<{ value: MeetingFileCategory; label: string }> = [
  { value: 'attachment', label: 'ไฟล์นำเสนอ / เอกสารในที่ประชุม' },
  { value: 'preparation', label: 'ไฟล์ / เอกสารเตรียมก่อนประชุม' },
  { value: 'completed_work', label: 'ผลงานแล้วเสร็จ' },
  { value: 'meeting_report', label: 'รายงานการประชุม' },
  { value: 'summary', label: 'สรุปการประชุม' },
  { value: 'transcript', label: 'Transcript / ถอดเสียง' },
  { value: 'audio_recording', label: 'ไฟล์เสียง' },
  { value: 'other', label: 'อื่น ๆ (เก็บในกลุ่มเตรียมประชุม)' },
];

const fileGroups: Array<{ title: string; description: string; categories: MeetingFileCategory[] }> =
  [
    {
      title: 'ไฟล์นำเสนอ / เอกสารในที่ประชุม',
      description: 'เอกสารที่ใช้ประกอบหรือเผยแพร่ระหว่างการประชุม',
      categories: ['attachment'],
    },
    {
      title: 'ไฟล์ / เอกสารเตรียมก่อนประชุม',
      description: 'วาระ เอกสารเตรียมงาน และไฟล์ที่ยังไม่ได้จัดหมวดเดิม',
      categories: ['preparation', 'other'],
    },
    {
      title: 'สรุป / รายงานหลังประชุม',
      description: 'สรุป รายงาน Transcript และไฟล์เสียงจากการประชุม',
      categories: ['meeting_report', 'summary', 'transcript', 'audio_recording'],
    },
    {
      title: 'ผลงานแล้วเสร็จ',
      description: 'ชิ้นงาน เอกสารส่งมอบ และผลลัพธ์ที่ดำเนินการเสร็จแล้ว',
      categories: ['completed_work'],
    },
  ];

const mimeByExtension: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  srt: 'text/plain',
  vtt: 'text/vtt',
  md: 'text/markdown',
  markdown: 'text/markdown',
  csv: 'text/csv',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  mp4: 'audio/mp4',
  wav: 'audio/wav',
  webm: 'audio/webm',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  aac: 'audio/aac',
  flac: 'audio/flac',
};

function normalizedContentType(file: File): string {
  const extension = file.name.split('.').at(-1)?.toLowerCase() ?? '';
  return mimeByExtension[extension] || file.type || 'application/octet-stream';
}

function suggestCategory(file: File): MeetingFileCategory {
  const name = file.name.toLocaleLowerCase('th-TH');
  const contentType = normalizedContentType(file);
  if (contentType.startsWith('audio/')) return 'audio_recording';
  if (/transcript|ถอดเสียง|คำบรรยาย/.test(name)) return 'transcript';
  if (/summary|สรุป/.test(name)) return 'summary';
  if (/minutes|meeting.?report|รายงานการประชุม|บันทึกการประชุม/.test(name)) return 'meeting_report';
  if (/final|deliverable|completed|ผลงานแล้วเสร็จ|ส่งมอบ/.test(name)) return 'completed_work';
  if (/agenda|วาระ|prepare|preparation|เตรียม/.test(name)) return 'preparation';
  if (/presentation|slide|slides|นำเสนอ|เอกสารประกอบ|attachment|\.pptx?$/.test(name))
    return 'attachment';
  return 'preparation';
}

function purposeForCategory(category: MeetingFileCategory) {
  if (category === 'transcript') return 'meeting_transcript' as const;
  if (category === 'summary') return 'meeting_summary' as const;
  return 'meeting_attachment' as const;
}

function documentTypeForCategory(category: MeetingFileCategory) {
  if (category === 'transcript') return 'transcript' as const;
  if (category === 'summary') return 'summary' as const;
  return 'attachment' as const;
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
}

function canSyncLinkToCalendar(value: string): boolean {
  const url = new URL(value);
  return url.hostname === 'docs.google.com' && (
    url.pathname.startsWith('/document/d/') || url.pathname.startsWith('/spreadsheets/d/') ||
    url.pathname.startsWith('/presentation/d/')
  );
}

async function responseError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
  return new Error(body?.error?.message ?? `อัปโหลดไม่สำเร็จ (HTTP ${response.status})`);
}

async function uploadPartWithTimeout(url: string, body: Blob): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 25_000);
  try {
    return await fetch(apiUrl(url), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/octet-stream' },
      body,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

export function MeetingFilesPanel({
  meetingId,
  appointmentId,
  syncToCalendar = true,
}: {
  meetingId?: string;
  appointmentId?: string;
  syncToCalendar?: boolean;
}) {
  const entityType = appointmentId ? 'appointment' : 'meeting';
  const entityId = appointmentId ?? meetingId!;
  const fileQueryKey = `${entityType}-files`;
  const client = useQueryClient();
  const [queue, setQueue] = useState<UploadCandidate[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingFileName, setEditingFileName] = useState('');
  const [editingCategory, setEditingCategory] = useState<MeetingFileCategory>('attachment');
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkCategory, setLinkCategory] = useState<MeetingFileCategory>('preparation');
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const files = useQuery({
    queryKey: [fileQueryKey, entityId],
    queryFn: () =>
      api<{ data: StoredMeetingFile[] }>(
        `/api/files?linkedEntityType=${entityType}&linkedEntityId=${encodeURIComponent(entityId)}&limit=100`,
      ).then((result) => result.data),
  });
  const links = useQuery({
    queryKey: [fileQueryKey, entityId, 'links'],
    queryFn: () => api<{ data: ExternalDocumentLink[] }>(
      `/api/files/links?linkedEntityType=${entityType}&linkedEntityId=${encodeURIComponent(entityId)}`,
    ).then((result) => result.data),
  });
  const saveLink = useMutation({
    mutationFn: () => api(editingLinkId ? `/api/files/links/${editingLinkId}` : '/api/files/links', {
      method: editingLinkId ? 'PATCH' : 'POST',
      body: JSON.stringify(editingLinkId
        ? { title: linkTitle.trim(), url: linkUrl.trim(), category: linkCategory }
        : { linkedEntityType: entityType, linkedEntityId: entityId, title: linkTitle.trim(), url: linkUrl.trim(), category: linkCategory }),
    }),
    onSuccess: async () => {
      setShowLinkForm(false);
      setEditingLinkId(null);
      setLinkTitle('');
      setLinkUrl('');
      await Promise.all([
        client.invalidateQueries({ queryKey: [fileQueryKey, entityId, 'links'] }),
        client.invalidateQueries({ queryKey: ['meeting-tree'] }),
      ]);
    },
  });
  const removeLink = useMutation({
    mutationFn: (linkId: string) => api(`/api/files/links/${linkId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [fileQueryKey, entityId, 'links'] }),
        client.invalidateQueries({ queryKey: ['meeting-tree'] }),
      ]);
    },
  });
  const syncLink = useMutation({
    mutationFn: (linkId: string) => api(`/api/files/links/${linkId}/sync-calendar`, { method: 'POST' }),
    onSettled: () => client.invalidateQueries({ queryKey: [fileQueryKey, entityId, 'links'] }),
  });
  const removeFile = useMutation({
    mutationFn: (fileId: string) => api(`/api/files/${fileId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [fileQueryKey, entityId] }),
        client.invalidateQueries({ queryKey: ['meeting-tree'] }),
      ]);
    },
  });
  const updateFile = useMutation({
    mutationFn: ({
      fileId,
      fileName,
      category,
    }: {
      fileId: string;
      fileName: string;
      category: MeetingFileCategory;
    }) =>
      api(`/api/files/${fileId}`, {
        method: 'PATCH',
        body: JSON.stringify({ fileName, category }),
      }),
    onSuccess: async () => {
      setEditingFileId(null);
      setEditingFileName('');
      await Promise.all([
        client.invalidateQueries({ queryKey: [fileQueryKey, entityId] }),
        client.invalidateQueries({ queryKey: ['meeting-tree'] }),
      ]);
    },
  });
  const syncFile = useMutation({
    mutationFn: (fileId: string) =>
      api(`/api/files/${fileId}/sync-calendar`, {
        method: 'POST',
        body: JSON.stringify({ appointmentId }),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: [fileQueryKey, entityId] }),
  });

  const beginEditingFile = (file: StoredMeetingFile) => {
    setEditingFileId(file.id);
    setEditingFileName(file.fileName);
    setEditingCategory(file.category);
    updateFile.reset();
  };

  const beginEditingLink = (link: ExternalDocumentLink) => {
    setEditingLinkId(link.id);
    setLinkTitle(link.title);
    setLinkUrl(link.url);
    setLinkCategory(link.category);
    setShowLinkForm(true);
    saveLink.reset();
  };

  const updateCandidate = (candidateId: string, patch: Partial<UploadCandidate>) => {
    setQueue((current) =>
      current.map((item) => (item.id === candidateId ? { ...item, ...patch } : item)),
    );
  };

  const chooseFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    setQueue((current) => [
      ...current.filter((item) => item.status !== 'done'),
      ...selected.map((file) => ({
        id: crypto.randomUUID(),
        file,
        category: suggestCategory(file),
        status: 'ready' as const,
        progress: 0,
        error: null,
        message: undefined,
      })),
    ]);
    event.target.value = '';
  };

  const hashFile = async (candidate: UploadCandidate): Promise<string> => {
    // Do not use a WebAssembly hash here. Some deployments apply a strict CSP
    // that blocks WebAssembly compilation before the upload request reaches the Worker.
    updateCandidate(candidate.id, { progress: 4, message: 'กำลังตรวจความถูกต้องของไฟล์…' });
    const bytes = await candidate.file.arrayBuffer();
    updateCandidate(candidate.id, { progress: 12, message: 'กำลังสร้างลายเซ็นไฟล์…' });
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    updateCandidate(candidate.id, {
      progress: 20,
      message: 'ตรวจสอบไฟล์เรียบร้อย กำลังเริ่มอัปโหลด…',
    });
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  };

  const uploadCandidate = async (candidate: UploadCandidate) => {
    let uploadCompleted = false;
    try {
      if (candidate.file.size > 200 * 1024 * 1024)
        throw new Error('ไฟล์ประชุมต้องมีขนาดไม่เกิน 200 MB');
      updateCandidate(candidate.id, {
        status: 'hashing',
        progress: 1,
        error: null,
        message: 'กำลังตรวจสอบไฟล์…',
      });
      const sha256 = await hashFile(candidate);
      updateCandidate(candidate.id, {
        status: 'uploading',
        progress: 20,
        message: 'กำลังเริ่มต้นอัปโหลด…',
      });
      const initiated = await api<{ data: UploadInit }>('/api/files/uploads', {
        method: 'POST',
        body: JSON.stringify({
          fileName: candidate.file.name,
          contentType: normalizedContentType(candidate.file),
          sizeBytes: candidate.file.size,
          sha256,
          purpose: purposeForCategory(candidate.category),
          category: candidate.category,
          linkedEntityType: entityType,
          linkedEntityId: entityId,
        }),
      });
      const storedFileId = initiated.data.id;
      if (initiated.data.resumed) {
        updateCandidate(candidate.id, {
          message: 'พบการอัปโหลดเดิม กำลังอัปโหลดต่อจากไฟล์เดิม…',
        });
      }
      if (initiated.data.provider === 'r2' && initiated.data.uploadUrl) {
        const response = await fetch(apiUrl(initiated.data.uploadUrl), {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': normalizedContentType(candidate.file) },
          body: candidate.file,
        });
        if (!response.ok) throw await responseError(response);
        uploadCompleted = true;
        updateCandidate(candidate.id, { progress: 95 });
      } else {
        const partBytes = initiated.data.partBytes;
        const totalParts = initiated.data.totalParts;
        if (!partBytes || !totalParts || !initiated.data.completeUrl) {
          throw new Error('ข้อมูลเริ่มอัปโหลด NAS ไม่ครบถ้วน');
        }
        for (let part = 1; part <= totalParts; part += 1) {
          updateCandidate(candidate.id, {
            progress: 20 + Math.round(((part - 1) / totalParts) * 70),
            message: `กำลังส่งส่วนที่ ${part}/${totalParts} — หากเกิน 25 วินาที ระบบจะแจ้งให้ลองต่อ`,
          });
          let uploaded = false;
          let lastError: Error | null = null;
          for (let attempt = 1; attempt <= 3 && !uploaded; attempt += 1) {
            try {
              const response = await uploadPartWithTimeout(
                `/api/files/${storedFileId}/parts/${part}`,
                candidate.file.slice(
                  (part - 1) * partBytes,
                  Math.min(part * partBytes, candidate.file.size),
                ),
              );
              if (!response.ok) {
                const body = (await response.json().catch(() => null)) as {
                  error?: { message?: string };
                } | null;
                throw new Error(
                  body?.error?.message ??
                    `NAS รับไฟล์ส่วนที่ ${part} ไม่สำเร็จ (HTTP ${response.status})`,
                );
              }
              uploaded = true;
            } catch (error) {
              lastError = error instanceof Error ? error : new Error('เชื่อมต่อ NAS ไม่สำเร็จ');
              if (attempt < 3)
                await new Promise((resolve) => window.setTimeout(resolve, 500 * attempt));
            }
          }
          if (!uploaded) throw lastError ?? new Error(`NAS รับไฟล์ส่วนที่ ${part} ไม่สำเร็จ`);
          updateCandidate(candidate.id, {
            progress: 20 + Math.round((part / totalParts) * 70),
            message: `ส่งส่วนที่ ${part}/${totalParts} แล้ว`,
          });
        }
        await api(initiated.data.completeUrl, { method: 'POST' });
        uploadCompleted = true;
      }
      updateCandidate(candidate.id, {
        status: 'linking',
        progress: 96,
        message: appointmentId
          ? syncToCalendar
            ? 'กำลังแนบไฟล์ใน Google Calendar…'
            : 'กำลังบันทึกไฟล์ในระบบ…'
          : 'กำลังเชื่อมไฟล์เข้ากับการประชุม…',
      });
      if (appointmentId && syncToCalendar) {
        await api(`/api/files/${storedFileId}/sync-calendar`, {
          method: 'POST',
          body: JSON.stringify({ appointmentId }),
        });
      } else if (meetingId) {
        await api(`/api/meetings/${meetingId}/documents`, {
          method: 'POST',
          body: JSON.stringify({
            storedFileId,
            documentType: documentTypeForCategory(candidate.category),
            processingStatus: 'uploaded',
            aiSuggestions: {},
          }),
        });
      }
      updateCandidate(candidate.id, {
        status: 'done',
        progress: 100,
        error: null,
        message: undefined,
      });
    } catch (error) {
      if (uploadCompleted) {
        updateCandidate(candidate.id, {
          status: 'done',
          progress: 100,
          error: 'อัปโหลดไฟล์แล้ว แต่ยังไม่ได้สร้างข้อมูลสำหรับประมวลผลอัตโนมัติ',
        });
        return;
      }
      const message =
        error instanceof ApiClientError && error.code === 'FILE_DUPLICATE'
          ? 'ไฟล์นี้มีอยู่แล้ว ระบบจึงไม่อัปโหลดซ้ำ'
          : error instanceof DOMException && error.name === 'AbortError'
            ? 'การส่งไฟล์ส่วนหนึ่งเกิน 25 วินาที ไฟล์ยังอยู่ในระบบ เลือกไฟล์เดิมแล้วกดอัปโหลดอีกครั้งเพื่อดำเนินการต่อ'
            : error instanceof Error
              ? error.message
              : 'อัปโหลดไม่สำเร็จ';
      updateCandidate(candidate.id, { status: 'failed', error: message });
    }
  };

  const uploadAll = async () => {
    setUploading(true);
    const ready = queue.filter((item) => item.status === 'ready' || item.status === 'failed');
    for (const candidate of ready) await uploadCandidate(candidate);
    await Promise.all([
      client.invalidateQueries({ queryKey: [fileQueryKey, entityId] }),
      ...(appointmentId
        ? [client.invalidateQueries({ queryKey: ['appointment', appointmentId] })]
        : [client.invalidateQueries({ queryKey: ['meeting-tree'] })]),
    ]);
    setUploading(false);
  };

  return (
    <section className="mt-5 border-t border-slate-200 pt-5">
      {!appointmentId && <PlaudImportPanel meetingId={meetingId!} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold">
            {appointmentId ? 'ไฟล์แนบรายการนัดหมาย' : 'ไฟล์ของการประชุม'}
          </h3>
          <p className="text-xs text-slate-500">
            {appointmentId
              ? syncToCalendar
                ? 'ไฟล์จะเก็บในระบบ แล้วแนบเข้า Google Calendar เดิมอัตโนมัติ'
                : 'ไฟล์จะเก็บในระบบก่อน เมื่อเพิ่มรายการลงปฏิทินแล้วจึงแนบไป Google Calendar ได้'
              : 'เพิ่มหลายไฟล์ได้ ระบบแนะนำหมวดให้และคุณเปลี่ยนได้ก่อนอัปโหลด'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => { setEditingLinkId(null); setLinkTitle(''); setLinkUrl(''); setLinkCategory('preparation'); setShowLinkForm(true); saveLink.reset(); }}>＋ แนบลิงก์</button>
          <label className="btn-secondary cursor-pointer">
            ＋ เลือกไฟล์
            <input
              className="sr-only"
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.pptx,.txt,.srt,.vtt,.md,.markdown,.csv,.jpg,.jpeg,.png,.webp,.mp3,.m4a,.mp4,.wav,.webm,.ogg,.oga,.aac,.flac"
              onChange={chooseFiles}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {showLinkForm && <form className="mt-4 space-y-3 rounded-2xl border border-teal-400 bg-slate-50 p-4" onSubmit={(event) => { event.preventDefault(); saveLink.mutate(); }}>
        <h4 className="font-bold text-slate-900">{editingLinkId ? 'แก้ไขลิงก์เอกสาร' : 'แนบลิงก์เอกสาร'}</h4>
        <p className="text-xs text-slate-700">เช่น Google Docs หรือ Google Sheets — ระบบเก็บลิงก์ ไม่คัดลอกไฟล์และไม่เปลี่ยนสิทธิ์ต้นฉบับ หากนัดอยู่ใน Google Calendar แล้ว ระบบจะแนบลิงก์ Docs/Sheets/Slides เข้า event ด้วย</p>
        <label className="block"><span className="label">ชื่อเอกสาร *</span><input className="field" value={linkTitle} onChange={(event) => setLinkTitle(event.target.value)} maxLength={255} required placeholder="เช่น ตารางงบประมาณ" /></label>
        <label className="block"><span className="label">ลิงก์ HTTPS *</span><input className="field" type="url" value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} maxLength={2048} required pattern="https://.+" placeholder="https://docs.google.com/..." /></label>
        <label className="block"><span className="label">กลุ่มเอกสาร</span><select className="field" value={linkCategory} onChange={(event) => setLinkCategory(event.target.value as MeetingFileCategory)}>{categories.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}</select></label>
        <div className="flex flex-wrap gap-2"><button className="btn-primary" type="submit" disabled={saveLink.isPending || !linkTitle.trim() || !linkUrl.trim()}>{saveLink.isPending ? 'กำลังบันทึก…' : 'บันทึกลิงก์'}</button><button className="btn-secondary" type="button" onClick={() => { setShowLinkForm(false); setEditingLinkId(null); saveLink.reset(); }}>ยกเลิก</button></div>
        {saveLink.error && <ErrorNotice message={saveLink.error.message} />}
      </form>}

      {queue.length > 0 && (
        <div className="mt-4 space-y-3 rounded-2xl bg-slate-50 p-3">
          {queue.map((candidate) => (
            <div key={candidate.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{candidate.file.name}</p>
                  <p className="text-xs text-slate-500">{formatBytes(candidate.file.size)}</p>
                </div>
                {!uploading && candidate.status !== 'done' && (
                  <button
                    type="button"
                    className="text-sm text-red-700"
                    onClick={() =>
                      setQueue((rows) => rows.filter((row) => row.id !== candidate.id))
                    }
                  >
                    นำออก
                  </button>
                )}
              </div>
              <select
                className="field mt-2"
                value={candidate.category}
                onChange={(event) =>
                  updateCandidate(candidate.id, {
                    category: event.target.value as MeetingFileCategory,
                  })
                }
                disabled={uploading || candidate.status === 'done'}
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {candidate.status !== 'ready' && (
                <div className="mt-2">
                  <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full bg-teal-600 transition-all"
                      style={{ width: `${candidate.progress}%` }}
                    />
                  </div>
                  <p
                    className={`mt-1 text-xs ${candidate.status === 'failed' ? 'text-red-700' : 'text-slate-500'}`}
                  >
                    {candidate.error ??
                      candidate.message ??
                      (candidate.status === 'hashing'
                        ? 'กำลังตรวจไฟล์ซ้ำ…'
                        : candidate.status === 'uploading'
                          ? 'กำลังอัปโหลด…'
                          : candidate.status === 'linking'
                            ? 'กำลังเชื่อมกับการประชุม…'
                            : 'อัปโหลดแล้ว')}
                  </p>
                </div>
              )}
            </div>
          ))}
          {queue.some((item) => item.status !== 'done') && (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => void uploadAll()}
              disabled={uploading}
            >
              {uploading
                ? 'กำลังอัปโหลดตามลำดับ…'
                : `อัปโหลด ${queue.filter((item) => item.status !== 'done').length} ไฟล์`}
            </button>
          )}
        </div>
      )}

      {files.isLoading || links.isLoading ? (
        <p className="mt-4 text-sm text-slate-500">กำลังโหลดเอกสาร…</p>
      ) : (
        <div className="mt-4 space-y-4">
          {fileGroups.map((group) => {
            const groupFiles = (files.data ?? []).filter(
              (file) => file.status === 'available' && group.categories.includes(file.category),
            );
            const groupLinks = (links.data ?? []).filter((link) => group.categories.includes(link.category));
            return (
              <section
                key={group.title}
                className="overflow-hidden rounded-xl border border-slate-200"
              >
                <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
                  <h4 className="text-sm font-bold text-slate-800">{group.title}</h4>
                  <p className="mt-0.5 text-xs text-slate-500">{group.description}</p>
                </div>
                {groupFiles.length === 0 && groupLinks.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-slate-500">ยังไม่มีไฟล์หรือลิงก์ในกลุ่มนี้</p>
                ) : (
                  <div className="divide-y divide-slate-100 px-3">
                    {groupFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          {editingFileId === file.id ? (
                            <div className="grid gap-2 sm:grid-cols-[minmax(12rem,1fr)_minmax(12rem,1fr)]">
                              <input
                                className="field"
                                value={editingFileName}
                                onChange={(event) => setEditingFileName(event.target.value)}
                                maxLength={255}
                                aria-label="ชื่อไฟล์"
                              />
                              <select
                                className="field"
                                value={editingCategory}
                                onChange={(event) =>
                                  setEditingCategory(event.target.value as MeetingFileCategory)
                                }
                                aria-label="กลุ่มไฟล์"
                              >
                                {categories.map((category) => (
                                  <option key={category.value} value={category.value}>
                                    {category.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <p className="truncate text-sm font-semibold">{file.fileName}</p>
                          )}
                          <p className="mt-1 text-xs text-slate-500">
                            {categories.find((category) => category.value === file.category)
                              ?.label ?? 'อื่น ๆ'}{' '}
                            · {formatBytes(file.sizeBytes)} ·{' '}
                            {file.provider === 'nas' ? 'NAS' : 'Private R2'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {editingFileId === file.id ? (
                            <>
                              <button
                                type="button"
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                                onClick={() => {
                                  setEditingFileId(null);
                                  updateFile.reset();
                                }}
                                disabled={updateFile.isPending}
                              >
                                ยกเลิก
                              </button>
                              <button
                                type="button"
                                className="rounded-lg bg-teal-700 px-3 py-2 text-sm font-semibold text-white"
                                onClick={() =>
                                  updateFile.mutate({
                                    fileId: file.id,
                                    fileName: editingFileName.trim(),
                                    category: editingCategory,
                                  })
                                }
                                disabled={!editingFileName.trim() || updateFile.isPending}
                              >
                                {updateFile.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                                onClick={() => beginEditingFile(file)}
                              >
                                แก้ไข
                              </button>
                              <a
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                                href={apiUrl(`/api/files/${file.id}/content?inline=true`)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                ดู/ฟัง
                              </a>
                              <a
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                                href={apiUrl(`/api/files/${file.id}/content`)}
                              >
                                ดาวน์โหลด
                              </a>
                              {appointmentId &&
                                syncToCalendar &&
                                file.calendarSyncStatus !== 'synced' && (
                                  <button
                                    type="button"
                                    className="rounded-lg border border-teal-300 px-3 py-2 text-sm font-semibold text-teal-800"
                                    onClick={() => syncFile.mutate(file.id)}
                                    disabled={syncFile.isPending}
                                  >
                                    {syncFile.isPending && syncFile.variables === file.id
                                      ? 'กำลังแนบ…'
                                      : 'แนบใน Calendar'}
                                  </button>
                                )}
                              <button
                                type="button"
                                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700"
                                onClick={() => {
                                  if (confirm(`ลบไฟล์ ${file.fileName}?`))
                                    removeFile.mutate(file.id);
                                }}
                                disabled={removeFile.isPending}
                              >
                                ลบ
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {groupLinks.map((link) => <div key={`link-${link.id}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0"><p className="truncate text-sm font-semibold">{link.title}</p><p className="mt-1 truncate text-xs text-slate-700">ลิงก์เอกสาร · {new URL(link.url).hostname}{link.calendarSyncStatus === 'synced' ? ' · อยู่ใน Google Calendar' : ''}</p>{link.calendarSyncStatus === 'failed' && <p className="mt-1 text-xs text-red-700">แนบลิงก์ใน Calendar ไม่สำเร็จ: {link.calendarSyncError ?? 'กรุณาลองใหม่'}</p>}</div>
                      <div className="flex flex-wrap gap-2">
                        <a className="rounded-lg border border-teal-400 px-3 py-2 text-sm font-semibold text-teal-800" href={link.url} target="_blank" rel="noopener noreferrer">เปิดต้นฉบับ ↗</a>
                        <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold" onClick={() => beginEditingLink(link)}>แก้ไข</button>
                        {appointmentId && syncToCalendar && (canSyncLinkToCalendar(link.url) || link.calendarSyncStatus === 'failed') && link.calendarSyncStatus !== 'synced' && <button type="button" className="rounded-lg border border-teal-400 px-3 py-2 text-sm font-semibold text-teal-800" disabled={syncLink.isPending} onClick={() => syncLink.mutate(link.id)}>ซิงก์ Calendar อีกครั้ง</button>}
                        <button type="button" className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700" disabled={removeLink.isPending} onClick={() => { if (confirm(`ลบลิงก์ ${link.title}?`)) removeLink.mutate(link.id); }}>ลบ</button>
                      </div>
                    </div>)}
                  </div>
                )}
              </section>
            );
          })}
          {(files.data ?? [])
            .filter((file) => file.status === 'uploading')
            .map((file) => (
              <div key={file.id} className="py-3 text-sm">
                <p className="font-semibold">{file.fileName}</p>
                <p className="mt-1 text-amber-700">
                  อัปโหลดค้างอยู่ — เลือกไฟล์เดิมอีกครั้ง แล้วกดอัปโหลดเพื่อดำเนินการต่อ
                </p>
              </div>
            ))}
          {(files.data ?? []).length === 0 && (links.data ?? []).length === 0 && (
            <p className="py-4 text-sm text-slate-500">ยังไม่มีไฟล์หรือลิงก์เพิ่มเติมในรายการนี้</p>
          )}
        </div>
      )}
      {(files.error || links.error || removeFile.error || updateFile.error || syncFile.error || removeLink.error || syncLink.error) && (
        <ErrorNotice
          message={
            (files.error ?? links.error ?? removeFile.error ?? updateFile.error ?? syncFile.error ?? removeLink.error ?? syncLink.error)?.message ??
            'จัดการเอกสารไม่สำเร็จ'
          }
        />
      )}
    </section>
  );
}
